import React, { memo, useState, useCallback, useRef, useEffect, useContext } from "react";
import {
    Navbar,
    NavbarBrand,
    NavbarHiddenOnMobile,
    Input,
    Avatar,
    Button,
    Icon
} from "@pmi/nyx-design-system";
import saLog from '../../assets/images/sa-logo.png';
import profileImg from '../../assets/images/profile_img.png';
import { MapviewContext } from "../../context/mapviewcontext";
import html2canvas from "html2canvas";
import { ApiContext } from "../../context/apicontext";

// Export Dropdown
const ExportOption = ({ label, onClick }) => (
    <div className="border-bottom py-2" onClick={onClick} style={{ cursor: "pointer" }}>
        <Icon color="black" name="download" /> {label}
    </div>
);

// All header code
const Header = memo(() => {
    const [isCardOpen, setIsCardOpen] = useState(false);
    const exportRef = useRef(null);
    const { searchBarData } = useContext(MapviewContext);
    const [searchText, setSearchText] = useState("");
    const [filteredResults, setFilteredResults] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const {leftdemographicsData} = useContext(ApiContext);

    const toggleCardOpen = useCallback(() => {
        setIsCardOpen(prev => !prev);
    }, []);

    const handleOutside = useCallback((event) => {
        if (exportRef.current && !exportRef.current.contains(event.target)) {
            setIsCardOpen(false);
        }
    }, []);

    useEffect(() => {
        if (isCardOpen) {
            document.addEventListener("mousedown", handleOutside);
        } else {
            document.removeEventListener("mousedown", handleOutside);
        }

        return () => document.removeEventListener("mousedown", handleOutside);
    }, [isCardOpen, handleOutside]);

    //search functionality
    const handleSearch = (event) => {
        const value = event.target.value;
        setSearchText(value);

        if (value.trim() === "") {
            setFilteredResults([]);
        } else {
            const results = searchBarData.filter(item =>
                item.name && item.name.toLowerCase().includes(value)
            );
            setFilteredResults(results);
        }
    };
    const handleSelectSearchResult = (item) => {
        setRecentSearches(prev => {
            const updatedSearches = [item, ...prev.filter(search => search.id !== item.id)];
            return updatedSearches;
        });

        setSearchText('');
    };

    // Map screenshot
    const handleMapScreenshot = async () => {
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
        document.body.appendChild(script);

        script.onload = async () => {
            const mapElement = document.querySelector(".map-container");
            if (!mapElement) {
                alert("Map not found!");
                return;
            }
            try {
                const canvas = await html2canvas(mapElement);
                const imageURL = canvas.toDataURL("image/png");

                const downloadLink = document.createElement("a");
                downloadLink.href = imageURL;
                downloadLink.download = "mapScreenshot.png";
                downloadLink.click();
            } catch (error) {
                console.error("Screenshot failed:", error);
            }
            setIsCardOpen(false);
        };
    }

    // FullScreenshot (Map and side panels)
    const handleFullScreenshot = async () => {
        setIsCardOpen(false); 
    
        setTimeout(async () => {
            try {
                const element = document.documentElement;
                const mapCanvas = document.querySelector(".azure-map-container canvas");
                let mapImageUrl = "";
    
                if (mapCanvas) {
                    mapImageUrl = mapCanvas.toDataURL("image/png");
                }
                const canvas = await html2canvas(element, {
                    useCORS: true,
                    scale: window.devicePixelRatio,
                    width: element.scrollWidth,
                    height: element.scrollHeight,
                    scrollX: -window.scrollX,
                    scrollY: -window.scrollY,
                    windowWidth: element.scrollWidth,
                    windowHeight: element.scrollHeight
                });
    
                if (mapImageUrl) {
                    const ctx = canvas.getContext("2d");
                    const mapImg = new Image();
                    mapImg.src = mapImageUrl;
                    await new Promise((resolve) => {
                        mapImg.onload = () => {
                            ctx.drawImage(mapImg, mapCanvas.offsetLeft, mapCanvas.offsetTop);
                            resolve();
                        };
                    });
                }
                const dataUrl = canvas.toDataURL("image/png");
                const link = document.createElement("a");
                link.href = dataUrl;
                link.download = "fullScreenshot.png";
                link.click();
            } catch (error) {
                console.error("Screenshot failed:", error);
            }
        }, 500);
    };

  // CSV file download (now download left side panel)
    const handleCSVFile = () => {
        if (!leftdemographicsData) {
          alert("Data not available for CSV.");
          return;
        }
      
        let csvContent = "data:text/csv;charset=utf-8,";
        // data convert to CSV format
        const convertToCSV = (categoryName, data) => {
          if (!data || data.length === 0) return "";
          
          let csvString = `\n${categoryName}\n`; 
          csvString += Object.keys(data[0]).join(",") + "\n"; 
          csvString += data.map(row => Object.values(row).join(",")).join("\n") + "\n";
          
          return csvString;
        };
      
        // append data in one file
        csvContent += convertToCSV("Demographics", leftdemographicsData.Demographics);
        csvContent += convertToCSV("Consumables", leftdemographicsData.Consumables);
        csvContent += convertToCSV("Kits", leftdemographicsData.Kits);
        // download file
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "CSV_file.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsCardOpen(false); 
      };
      
      // Excel file download (now download left side panel)
      const handleExcelFile = () => {
        if (!leftdemographicsData) {
            alert("Data not available for Excel.");
          return;
        }
      // data convert to CSV format
        const convertToTable = (categoryName, data) => {
          if (!data || data.length === 0) return "";
          let table = `<tr><th colspan="3">${categoryName}</th></tr>`; 
          table += "<tr>" + Object.keys(data[0]).map(key => `<th>${key}</th>`).join("") + "</tr>"; 
          table += data.map(row => "<tr>" + Object.values(row).map(value => `<td>${value}</td>`).join("") + "</tr>").join("");
      
          return table;
        };
      
        // create table for data
        let html = `
          <html>
          <head><meta charset="UTF-8"></head>
          <body>
            <table border="1">
              ${convertToTable("Demographics", leftdemographicsData.Demographics)}
              ${convertToTable("Consumables", leftdemographicsData.Consumables)}
              ${convertToTable("Kits", leftdemographicsData.Kits)}
            </table>
          </body>
          </html>`;
        const blob = new Blob([html], { type: "application/vnd.ms-excel" });
        const url = URL.createObjectURL(blob);
      
        // download file
        const link = document.createElement("a");
        link.href = url;
        link.download = "excel_file.xls";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsCardOpen(false); 
      };
      
    return (
        <Navbar>
            <NavbarBrand>
                <img src={saLog} height={48} width={48} alt="Store Advisor Logo" />
                <h5 className="navbarBrand">Store Advisor</h5>
            </NavbarBrand>
            <NavbarHiddenOnMobile>
                <div className="input-wrapper position-relative">
                    <Input placeholder="Search region, municipality, POS..." startIcon="search" className="search-input" value={searchText} onChange={handleSearch} />
                    {searchText && (
                        <div className="search-results position-absolute bg-white border p-2 mt-2 rounded shadow text-black w-100" style={{ zIndex: 9999 }}>
                            {/* Suggested Results */}
                            <div className="text-bold font-weight-bold px-2 mb-2">SUGGESTED RESULTS:</div>
                            {filteredResults.length > 0 ? (
                                filteredResults.map(item => (
                                    <div key={item.id} className="py-2 px-2 cursor-pointer border-bottom" onClick={() => handleSelectSearchResult(item)} >
                                        <div className="font-weight-bold">{item.name}</div>
                                        <div className="text-muted small">Market, Territory Level</div>
                                    </div>
                                ))
                            ) : (
                                <div className="py-1 px-2 text-muted">No results found</div>
                            )}
                            {/* Recent Searches */}
                            {recentSearches.length > 0 && (
                                <>
                                    <div className="text-muted font-weight-bold px-2">RECENT SEARCHES:</div>
                                    {recentSearches.map(item => (
                                        <div key={item.id} className="py-1 px-2 cursor-pointer" onClick={() => handleSelectSearchResult(item)}>
                                            <div className="font-weight-bold">{item.name}</div>
                                            <div className="text-muted small">Market, Territory Level</div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>
                <div className="exportWrapper">
                    <div className="position-relative" ref={exportRef}>
                        <Button outlined onClick={toggleCardOpen}>
                            <Icon color="primary" name="download" /> Export <Icon color="primary" className="mt-1" name={isCardOpen ? "chevron-up" : "chevron-down"} />
                        </Button>
                        {isCardOpen && (
                            <div className="export-panel rounded-3 position-absolute p-3 pb-2 text-black cursor-pointer mt-4">
                                <ExportOption label="Screenshot (Map)" onClick={handleMapScreenshot} />
                                <ExportOption label="Screenshot (Map and side panels)" onClick={handleFullScreenshot}/>
                                <ExportOption label="CSV file" onClick={handleCSVFile}/>
                                <ExportOption label="Excel File" onClick={handleExcelFile}/>
                            </div>
                        )}
                    </div>
                    <div>
                        <Avatar
                            imageUrl={profileImg}
                            menuItems={[{ label: 'Log out', url: '/logout' }]}
                            textColor="black"
                            userName="John Appleseed"
                        />
                    </div>
                </div>
            </NavbarHiddenOnMobile>
        </Navbar>
    );
});

export default Header;